import { useAuth } from 'react-oidc-context';
import { Navigate } from 'react-router-dom';
import { isOidcConfigured } from '../../lib/auth';
import { Loader2, Shield } from 'lucide-react';
import { Button } from '../ui/Button';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Wraps a route that requires OneLogin authentication.
 *
 * - If OIDC is not configured (dev mode), renders children directly.
 * - If user is authenticated, renders children.
 * - If user is not authenticated, shows a login prompt.
 * - While loading, shows a spinner.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  // If OIDC env vars aren't set, skip auth (dev mode)
  if (!isOidcConfigured()) {
    return <>{children}</>;
  }

  return <AuthGate>{children}</AuthGate>;
}

/**
 * Inner component that uses the useAuth hook (must be inside AuthProvider).
 */
function AuthGate({ children }: { children: React.ReactNode }) {
  const auth = useAuth();

  if (auth.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto" />
          <p className="text-gray-600">Verifying your identity...</p>
        </div>
      </div>
    );
  }

  if (auth.error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <Shield className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">Authentication Error</h2>
          <p className="text-gray-600 text-sm">
            {auth.error.message || 'Unable to verify your identity. Please try again.'}
          </p>
          <Button onClick={() => auth.signinRedirect()}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
